import {
  Body1Strong,
  createTableColumn,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  makeStyles,
  mergeClasses,
  SkeletonItem,
  tokens,
  type DataGridProps,
  type DataGridRowProps,
  type TableColumnDefinition,
} from '@fluentui/react-components'
import {
  forwardRef,
  useImperativeHandle,
  useState,
  type Dispatch,
  type ForwardedRef,
  type ReactElement,
  type ReactNode,
  type RefAttributes,
  type SetStateAction,
} from 'react'

import { useAppStyles } from '../styles/common.styles'

import { ErrorMessageBar } from './ErrorMessageBar'
import { InfoLayout } from './InfoLayout'

declare module '@fluentui/react-components' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableColumnDefinition<TItem> {
    minWidth?: number
    maxWidth?: number
    justifyContent?: 'flex-start' | 'center' | 'flex-end'
  }
}

export type TableColumn<T> = {
  /**
   * Unique column identifier used by Fluent UI table internals.
   */
  id: string
  /**
   * Fallback header text rendered in the column header cell.
   */
  header?: string
  /**
   * Optional custom header renderer for the column header cell.
   */
  renderHeaderCell?: () => ReactNode
  /**
   * Cell renderer for each row item.
   */
  renderCell: (item: T) => ReactNode
  /**
   * Optional comparator used when sorting this column.
   */
  compare?: ((a: T, b: T) => number) | undefined
  /**
   * Minimum width in pixels applied to header and data cells.
   */
  minWidth?: number | undefined
  /**
   * Maximum width in pixels applied to header and data cells.
   */
  maxWidth?: number | undefined
  /**
   * Horizontal alignment for cell content.
   */
  justifyContent?: 'flex-start' | 'center' | 'flex-end'
}

/**
 * Converts external column configuration to Fluent UI table column definitions.
 */
function toColumnsDef<T>(columns: TableColumn<T>[]): TableColumnDefinition<T>[] {
  return columns.map((col) => ({
    ...createTableColumn<T>({
      columnId: col.id,
      renderHeaderCell: col.renderHeaderCell ?? (() => col.header ?? null),
      renderCell: col.renderCell,
      compare: col.compare,
    }),
    minWidth: col.minWidth,
    maxWidth: col.maxWidth,
    justifyContent: col.justifyContent,
  }))
}

interface DataTableProps<T> {
  /**
   * Items to render as table rows.
   */
  data: T[]
  /**
   * Column configuration and cell renderers.
   */
  columns: TableColumn<T>[]
  /**
   * Enables row selection with single or multiselect behavior.
   */
  selectionMode?: 'single' | 'multiselect'
  /**
   * Optional row id resolver used for stable selection.
   */
  getRowId?: (item: T) => string | number
  /**
   * External selected rows state. When omitted, the component manages selection internally.
   */
  selectionState?: [Set<string | number>, Dispatch<SetStateAction<Set<string | number>>>]
  disabledItems?: Set<string | number>

  /**
   * Enables column sorting where a comparator is provided.
   */
  sortable?: boolean
  sortState?: DataGridProps['sortState']
  onSortChange?: DataGridProps['onSortChange']
  /**
   * Row click handler. It is not invoked when selection mode is enabled.
   */
  onRowClick?: (item: T) => void
  renderRow?: (item: T, row: ReactElement<DataGridRowProps<T>>) => ReactNode
  /**
   * Optional CSS class name applied to the table container.
   */
  className?: string
  /**
   * Size variant of the table, affecting row height and typography.
   */
  size?: 'small' | 'large'

  resizable?: boolean
  /**
   * Optional max height for the table, enabling vertical scrolling when exceeded.
   */
  maxHeight?: number | string
  /**
   * Disables row hover effect and pointer cursor on interactive rows.
   */
  noHover?: boolean
  /**
   * Indicates whether the table is currently loading data.
   */
  isLoading?: boolean
  /**
   * Indicates whether an error occurred while loading data.
   */
  isError?: boolean
  /**
   * Custom error content to display when isError is true. If not provided, a default error message bar is shown.
   */
  error?: ReactNode

  customEmptyMessage?: ReactNode
}

/**
 * DataTable component handle exposing imperative methods.
 */
export interface DataTableHandle {
  /**
   * Set of currently selected row ids, useful when selection is managed externally.
   */
  selectedItems: Set<string | number>
}

const useStyles = makeStyles({
  tableContainer: {
    '--colorSubtleBackgroundHover': tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadius2XLarge,
    overflowX: 'auto',
    isolation: 'isolate',
  },
  noHover: {
    '--colorSubtleBackgroundHover': tokens.colorNeutralBackground1,
    '--colorSubtleBackgroundPressed': tokens.colorNeutralBackground1,
  },
  table: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadius2XLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    clipPath: 'inset(-1px)',
    '& .fui-TableHeaderCell__button': { justifyContent: 'inherit' },
  },
  tableLarge: {
    '& .fui-DataGridHeaderCell': { padding: `0 ${tokens.spacingHorizontalL}` },
    '& .fui-DataGridCell': { padding: `0 ${tokens.spacingHorizontalL}`, minHeight: '52px' },
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  tableHeader: {
    height: '44px !important',
    backgroundColor: `${tokens.colorNeutralBackground2} !important`,
    borderRadius: `${tokens.borderRadius2XLarge} ${tokens.borderRadius2XLarge} 0 0`,
    color: tokens.colorPalettePlatinumForeground2,
  },
  tableHeaderLarge: {
    height: '52px !important',
  },
  tableBody: {
    '& .fui-DataGridRow:last-child': {
      borderRadius: `0 0 ${tokens.borderRadius2XLarge} ${tokens.borderRadius2XLarge}`,
      borderBottom: 'none',
    },
  },
  emptyTable: {
    padding: tokens.spacingVerticalL,
    minHeight: '529px', // table body height when 10 rows are rendered, adjust as needed
  },
})

/**
 * Generic Fluent UI data table with optional sorting, selection and row click handling.
 */
function DataTableInner<T>(props: DataTableProps<T>, ref: ForwardedRef<DataTableHandle>) {
  const styles = useStyles()
  const appStyles = useAppStyles()

  const {
    data,
    columns,
    selectionMode,
    getRowId,
    selectionState,
    sortable,
    onRowClick,
    disabledItems,
    noHover,
    size,
    isLoading,
    isError,
    error,
  } = props

  const columnsDef = toColumnsDef<T>(columns)

  const [internalSelectedRows, setInternalSelectedRows] = useState<Set<string | number>>(new Set())
  const [selectedRows, setSelectedRows] = selectionState ?? [
    internalSelectedRows,
    setInternalSelectedRows,
  ]

  useImperativeHandle(
    ref,
    () => ({
      selectedItems: selectedRows,
    }),
    [selectedRows],
  )

  const onSelectionChange: DataGridProps['onSelectionChange'] = (_, data) => {
    if (selectionState) {
      const filtered = new Set(data.selectedItems)
      disabledItems?.forEach((item) => filtered.delete(item))
      setSelectedRows(filtered)
    }
  }

  return (
    <div
      className={mergeClasses(styles.tableContainer, noHover && styles.noHover, props.className)}
    >
      <DataGrid
        items={data}
        columns={columnsDef}
        sortable={sortable}
        sortState={props.sortState}
        onSortChange={props.onSortChange}
        selectionAppearance="none"
        selectionMode={selectionMode}
        selectedItems={selectionState && selectedRows}
        onSelectionChange={selectionMode && onSelectionChange}
        getRowId={getRowId}
        className={mergeClasses(styles.table, size === 'large' && styles.tableLarge)}
        style={props.maxHeight ? { maxHeight: props.maxHeight } : undefined}
      >
        <DataGridHeader className={styles.stickyHeader}>
          <DataGridRow
            className={mergeClasses(
              styles.tableHeader,
              size === 'large' && styles.tableHeaderLarge,
            )}
            selectionCell={
              selectionMode && {
                checkboxIndicator: {
                  'aria-label': 'Select All',
                  disabled: data.some((item) => getRowId && disabledItems?.has(getRowId(item))),
                },
              }
            }
          >
            {({ renderHeaderCell, minWidth, maxWidth, justifyContent }) => (
              <DataGridHeaderCell style={{ minWidth, maxWidth, justifyContent }}>
                <Body1Strong>{renderHeaderCell()}</Body1Strong>
              </DataGridHeaderCell>
            )}
          </DataGridRow>
        </DataGridHeader>
        {isLoading && (
          <div>
            {Array.from({ length: 10 }).map((_, index) => (
              <DataGridRow<T> key={index}>
                {({ minWidth, maxWidth, justifyContent }) => (
                  <DataGridCell style={{ minWidth, maxWidth, justifyContent }}>
                    <SkeletonItem size={16} />
                  </DataGridCell>
                )}
              </DataGridRow>
            ))}
          </div>
        )}
        {isError && (
          <div className={styles.emptyTable}>
            {error ? (
              error
            ) : (
              <ErrorMessageBar
                title="Failed to load data"
                message="There was an error loading the data. Please try again."
              />
            )}
          </div>
        )}
        {data.length === 0 && !isLoading && !isError && (
          <>
            {props.customEmptyMessage ? (
              <div>{props.customEmptyMessage}</div>
            ) : (
              <div className={styles.emptyTable}>
                <InfoLayout
                  title="No data available"
                  message="There are no items to display in the table."
                />
              </div>
            )}
          </>
        )}
        {!isLoading && !isError && data.length > 0 && (
          <DataGridBody<T> className={styles.tableBody}>
            {({ item, rowId }) => {
              const row = (
                <DataGridRow<T>
                  key={rowId}
                  selectionCell={
                    selectionMode && {
                      checkboxIndicator: {
                        'aria-label': 'Select Row',
                        disabled: disabledItems?.has(rowId),
                      },
                    }
                  }
                  onClick={() => !selectionMode && onRowClick?.(item)}
                  className={mergeClasses((onRowClick || selectionMode) && appStyles.pointer)}
                >
                  {({ renderCell, minWidth, maxWidth, justifyContent }) => (
                    <DataGridCell style={{ minWidth, maxWidth, justifyContent }}>
                      {renderCell(item)}
                    </DataGridCell>
                  )}
                </DataGridRow>
              )
              return props.renderRow ? props.renderRow(item, row) : row
            }}
          </DataGridBody>
        )}
      </DataGrid>
    </div>
  )
}

const DataTable = forwardRef(DataTableInner) as <T>(
  props: DataTableProps<T> & RefAttributes<DataTableHandle>,
) => ReactElement

export default DataTable
